/**
 * Ideas Service
 *
 * HttpClient-aware API layer for ideas. All methods take an ``HttpClient``
 * first argument and branch URL construction on ``http.context.kind``:
 *
 * - admin  -> ``/api/clients/{clientId}/ideas`` for list endpoints
 * - portal -> ``/api/client-portal/ideas`` (client_id is derived from JWT)
 *
 * Endpoints addressed by idea id are the same for both contexts.
 */

import { apiClient } from "@/services/apiClient";
import type { HttpClient, HttpContext } from "@/core/http/types";
import {
  Idea,
  IdeaResource,
  IdeaStatus,
  IdeasResponse,
  CreateIdeaRequest,
  UpdateIdeaRequest,
} from "../types";

function handleError(error: unknown, defaultMessage: string): never {
  console.error(`IdeasService Error: ${defaultMessage}`, error);
  if (error instanceof Error) throw error;
  throw new Error(defaultMessage);
}

function buildListUrl(ctx: HttpContext, qs: string): string {
  const base =
    ctx.kind === "portal"
      ? "/api/client-portal/ideas"
      : `/api/clients/${ctx.clientId}/ideas`;
  return qs ? `${base}?${qs}` : base;
}

export const ideasService = {
  async getIdeas(
    http: HttpClient,
    status?: IdeaStatus | string,
    includePosts = false,
    includeLinks = false
  ): Promise<Idea[]> {
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (includePosts) params.append("include_posts", "true");
      if (includeLinks) params.append("include_links", "true");
      const url = buildListUrl(http.context, params.toString());
      const response = await http.get<IdeasResponse>(url);
      return response.ideas;
    } catch (error) {
      handleError(error, "Failed to fetch ideas");
    }
  },

  async getIdea(
    http: HttpClient,
    ideaId: string,
    includeNotes = false,
    includePosts = false,
    includeLinks = false
  ): Promise<Idea> {
    try {
      const params = new URLSearchParams();
      if (includeNotes) params.append("include_notes", "true");
      if (includePosts) params.append("include_posts", "true");
      if (includeLinks) params.append("include_links", "true");
      const qs = params.toString();
      const base =
        http.context?.kind === "portal"
          ? `/api/client-portal/ideas/${ideaId}`
          : `/api/ideas/${ideaId}`;
      const url = `${base}${qs ? `?${qs}` : ""}`;
      return await http.get<Idea>(url);
    } catch (error) {
      handleError(error, "Failed to fetch idea");
    }
  },

  async getApprovedIdeas(http: HttpClient, clientId: string): Promise<Idea[]> {
    try {
      const response = await http.get<IdeasResponse>(
        `/api/clients/${clientId}/ideas/approved`
      );
      return response.ideas;
    } catch (error) {
      handleError(error, "Failed to fetch approved ideas");
    }
  },

  async createIdea(
    http: HttpClient,
    clientId: string,
    data: CreateIdeaRequest
  ): Promise<Idea> {
    try {
      return await http.post<Idea>(`/api/clients/${clientId}/ideas`, data);
    } catch (error) {
      handleError(error, "Failed to create idea");
    }
  },

  async updateIdea(
    http: HttpClient,
    ideaId: string,
    data: UpdateIdeaRequest
  ): Promise<Idea> {
    try {
      const url =
        http.context?.kind === "portal"
          ? `/api/client-portal/ideas/${ideaId}`
          : `/api/ideas/${ideaId}`;
      return await http.patch<Idea>(url, data);
    } catch (error) {
      handleError(error, "Failed to update idea");
    }
  },

  async deleteIdea(http: HttpClient, ideaId: string): Promise<void> {
    try {
      await http.delete(`/api/ideas/${ideaId}`);
    } catch (error) {
      handleError(error, "Failed to delete idea");
    }
  },

  async submitForApproval(http: HttpClient, ideaId: string): Promise<Idea> {
    try {
      return await http.post<Idea>(`/api/ideas/${ideaId}/submit`);
    } catch (error) {
      handleError(error, "Failed to submit idea for approval");
    }
  },

  async revertToDraft(http: HttpClient, ideaId: string): Promise<Idea> {
    try {
      return await http.post<Idea>(`/api/ideas/${ideaId}/revert`);
    } catch (error) {
      handleError(error, "Failed to revert idea to draft");
    }
  },

  async addResource(
    http: HttpClient,
    ideaId: string,
    resource: IdeaResource
  ): Promise<Idea> {
    try {
      const idea = await ideasService.getIdea(http, ideaId);
      const updatedResources = [...idea.resources, resource];
      return ideasService.updateIdea(http, ideaId, { resources: updatedResources });
    } catch (error) {
      handleError(error, "Failed to add resource to idea");
    }
  },

  async removeResource(
    http: HttpClient,
    ideaId: string,
    resourceIndex: number
  ): Promise<Idea> {
    try {
      const idea = await ideasService.getIdea(http, ideaId);
      const updatedResources = idea.resources.filter((_, i) => i !== resourceIndex);
      return ideasService.updateIdea(http, ideaId, { resources: updatedResources });
    } catch (error) {
      handleError(error, "Failed to remove resource from idea");
    }
  },

  async linkNote(http: HttpClient, ideaId: string, noteId: string): Promise<void> {
    try {
      await http.post(`/api/ideas/${ideaId}/notes/${noteId}`);
    } catch (error) {
      handleError(error, "Failed to link note to idea");
    }
  },

  async unlinkNote(http: HttpClient, ideaId: string, noteId: string): Promise<void> {
    try {
      await http.delete(`/api/ideas/${ideaId}/notes/${noteId}`);
    } catch (error) {
      handleError(error, "Failed to unlink note from idea");
    }
  },

  async setBrief(
    http: HttpClient,
    ideaId: string,
    briefId: string | null
  ): Promise<Idea> {
    try {
      return await http.put<Idea>(`/api/ideas/${ideaId}/brief`, { briefId });
    } catch (error) {
      handleError(error, "Failed to set brief on idea");
    }
  },

  async uploadImage(http: HttpClient, ideaId: string, file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await http.postFormData<{ url: string }>(
        `/api/ideas/${ideaId}/upload-image`,
        formData
      );
      return response.url;
    } catch (error) {
      handleError(error, "Failed to upload image");
    }
  },

  async duplicateIdea(http: HttpClient, ideaId: string): Promise<Idea> {
    try {
      return await http.post<Idea>(`/api/ideas/${ideaId}/duplicate`);
    } catch (error) {
      handleError(error, "Failed to duplicate idea");
    }
  },

  /** Portal-only: get-or-create the collection linked to an idea so a
   * creator upload lands organized. Admin uses useIdeaCollection. */
  async ensureIdeaCollection(http: HttpClient, ideaId: string): Promise<string> {
    const response = await http.post<{ collectionId: string }>(
      `/api/client-portal/ideas/${ideaId}/collection`,
      {}
    );
    return response.collectionId;
  },
};

/**
 * Legacy default export preserved for non-React callsites. Binds to the
 * admin ``apiClient`` singleton. Prefer ``useHttpClient()`` + method calls
 * inside React components/hooks.
 */
const legacyAdmin = {
  getIdeas: (c: string, s?: IdeaStatus, p = false, l = false) =>
    ideasService.getIdeas(apiClient as unknown as HttpClient, s, p, l),
  getIdea: (id: string, n = false, p = false, l = false) =>
    ideasService.getIdea(apiClient as unknown as HttpClient, id, n, p, l),
  getApprovedIdeas: (c: string) =>
    ideasService.getApprovedIdeas(apiClient as unknown as HttpClient, c),
  createIdea: (c: string, d: CreateIdeaRequest) =>
    ideasService.createIdea(apiClient as unknown as HttpClient, c, d),
  updateIdea: (id: string, d: UpdateIdeaRequest) =>
    ideasService.updateIdea(apiClient as unknown as HttpClient, id, d),
  deleteIdea: (id: string) =>
    ideasService.deleteIdea(apiClient as unknown as HttpClient, id),
  submitForApproval: (id: string) =>
    ideasService.submitForApproval(apiClient as unknown as HttpClient, id),
  revertToDraft: (id: string) =>
    ideasService.revertToDraft(apiClient as unknown as HttpClient, id),
  addResource: (id: string, r: IdeaResource) =>
    ideasService.addResource(apiClient as unknown as HttpClient, id, r),
  removeResource: (id: string, i: number) =>
    ideasService.removeResource(apiClient as unknown as HttpClient, id, i),
  linkNote: (id: string, n: string) =>
    ideasService.linkNote(apiClient as unknown as HttpClient, id, n),
  unlinkNote: (id: string, n: string) =>
    ideasService.unlinkNote(apiClient as unknown as HttpClient, id, n),
  setBrief: (id: string, b: string | null) =>
    ideasService.setBrief(apiClient as unknown as HttpClient, id, b),
  uploadImage: (id: string, f: File) =>
    ideasService.uploadImage(apiClient as unknown as HttpClient, id, f),
  duplicateIdea: (id: string) =>
    ideasService.duplicateIdea(apiClient as unknown as HttpClient, id),
};

export default legacyAdmin;
